import React, { createContext, useContext, useCallback, useState, useEffect } from "react";
import { listPlanReviews, type PlanReviewListItem } from "@/api/plan_review";
import { useMunicipality } from "@/context/MunicipalityContext";

type PlanReviewContextValue = {
  selectedReviewId: number | null;
  setSelectedReviewId: (id: number | null) => void;
  reviews: PlanReviewListItem[];
  setReviews: React.Dispatch<React.SetStateAction<PlanReviewListItem[]>>;
  /**
   * Reloads the list and resolves with it. When the request fails it resolves
   * with [] but leaves the list the sidebar already shows alone.
   */
  refreshReviews: () => Promise<PlanReviewListItem[]>;
};

const PlanReviewContext = createContext<PlanReviewContextValue | null>(null);

function PlanReviewProviderInner({
  children,
  municipalityId,
}: {
  children: React.ReactNode;
  municipalityId: number | null;
}) {
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [reviews, setReviews] = useState<PlanReviewListItem[]>([]);

  const refreshReviews = useCallback(async () => {
    try {
      // Both kinds: the sidebar shows only the unlogged ones, but the
      // Launch Pad resume feed and the module summary tiles count a
      // planner's logged runs too, so the narrowing happens in the list
      // rather than here.
      const list = await listPlanReviews({
        scope: "all",
        ...(municipalityId ? { municipalityId } : {}),
      });
      setReviews(list);
      return list;
    } catch {
      // A failed refresh says nothing about what the planner has; emptying
      // the sidebar here would hide runs right when the wizard tells them
      // to look there.
      return [];
    }
  }, [municipalityId]);

  useEffect(() => {
    refreshReviews();
  }, [refreshReviews]);

  const value: PlanReviewContextValue = {
    selectedReviewId,
    setSelectedReviewId,
    reviews,
    setReviews,
    refreshReviews,
  };

  return <PlanReviewContext.Provider value={value}>{children}</PlanReviewContext.Provider>;
}

export function PlanReviewProvider({ children }: { children: React.ReactNode }) {
  const { municipality } = useMunicipality();
  return (
    <PlanReviewProviderInner municipalityId={municipality?.id ?? null}>
      {children}
    </PlanReviewProviderInner>
  );
}

export function usePlanReview() {
  const ctx = useContext(PlanReviewContext);
  if (!ctx) throw new Error("usePlanReview must be used within PlanReviewProvider");
  return ctx;
}
